import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, ChevronRight } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { getNotificationsForUser, getReadNotificationsForUser, getUnreadCountForUser, markNotificationRead } from '../utils/notifications';

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const username = user?.telegramName;
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refreshNotifications = async () => {
    if (!username) return;
    try {
      const nextNotifications = await getNotificationsForUser(username);
      setNotifications(nextNotifications);
      const count = await getUnreadCountForUser(username);
      setUnreadCount(count);
      const readIdsList = await getReadNotificationsForUser(username);
      setReadIds(readIdsList);
      if (selectedNotification) {
        const refreshed = nextNotifications.find((item) => item.id === selectedNotification.id);
        setSelectedNotification(refreshed || null);
      }
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const handleSelect = async (notification) => {
    if (!username) return;
    await markNotificationRead(username, notification.id);
    setSelectedNotification(notification);
    await refreshNotifications();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition"
            >
              <ArrowLeft size={18} />
              <span>Back</span>
            </button>
            <div className="text-center">
              <p className="text-sm text-slate-500">Notifications</p>
              <h1 className="text-xl font-semibold text-slate-900">Inbox</h1>
            </div>
            <div className="text-right text-sm text-slate-500">
              {unreadCount > 0 ? `${unreadCount} unread` : 'No unread messages'}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Your Notifications</p>
                <p className="text-xs text-slate-500 mt-1">Only admin-sent messages are shown here.</p>
              </div>
              <Bell className="text-slate-400" size={20} />
            </div>
            {notifications.length === 0 ? (
              <div className="p-10 text-center text-slate-500">
                <p className="text-lg font-semibold">No notifications available</p>
                <p className="text-sm mt-2">You’ll see messages from the admin here when they send them.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {notifications.map((notification) => {
                  const isSelected = selectedNotification?.id === notification.id;
                  const isUnread = !readIds.includes(notification.id);
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => handleSelect(notification)}
                      className={`w-full text-left px-6 py-4 transition ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-100'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-slate-900">{notification.subject}</p>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{notification.message}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400">{new Date(notification.createdAt).toLocaleDateString()}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {notification.targetType === 'all' ? (
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">All users</span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">Selected</span>
                            )}
                            {isUnread && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700">New</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-700">
                <Bell size={20} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Notification details</p>
                <p className="text-xs text-slate-500">Select a message to read the full content.</p>
              </div>
            </div>

            {!selectedNotification ? (
              <div className="rounded-3xl border border-dashed border-slate-200 p-6 text-center text-slate-500">
                <p className="font-semibold mb-2">No notification selected</p>
                <p className="text-sm">Tap any notification from the list to view the full details.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-slate-500">Sent by {selectedNotification.createdBy || 'Admin'}</div>
                <h2 className="text-xl font-semibold text-slate-900">{selectedNotification.subject}</h2>
                <p className="text-sm text-slate-500">{new Date(selectedNotification.createdAt).toLocaleString()}</p>
                <div className="rounded-3xl bg-slate-50 border border-slate-200 p-5 text-slate-700 whitespace-pre-line text-sm">
                  {selectedNotification.message}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedNotification(null)}
                  className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 text-sm font-semibold"
                >
                  <ChevronRight size={16} className="rotate-180" /> Clear selection
                </button>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default Notifications;
